import { Row, Col, Container, Card, Button, Badge, Form, InputGroup, Spinner } from "react-bootstrap";
import Layout from "../../components/Layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getResponses, respondToTicket } from "../../redux/slices/support.slice";
import toast from "react-hot-toast";
import dateFormatter from "../../utils/dateFormatter";

const TicketView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const messagesEndRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [replies, setReplies] = useState([]);
    const [ticket, setTicket] = useState({});
    const [payload, setPayload] = useState({ ticket_id: id, message: '' });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const loadResponse = useCallback((id) => {
        dispatch(getResponses(id))
            .then((res) => {
                if (res.payload?.data?.messages) {
                    const orderedMessages = [...res.payload.data.messages].reverse();
                    setReplies(orderedMessages);
                    // setReplies(res.payload.data.messages);
                }
                if (res.payload?.data?.ticket) {
                    setTicket(res.payload.data.ticket);
                }
            });
    }, [dispatch]);

    useEffect(() => {
        loadResponse(id);
    }, [id, loadResponse]);

    useEffect(() => {
        scrollToBottom();
    }, [replies]);

    const handleInput = (event) => {
        const { name, value } = event.target;
        setPayload(prev => ({ ...prev, [name]: value }));
    };

    const sendReply = () => {
        if (!payload.message) return;
        
        setLoading(true);
        dispatch(respondToTicket(payload))
            .then((response) => {
                if (response.payload.success) {
                    toast.success('Sent');
                    setPayload(prev => ({ ...prev, message: '' }));
                } else {
                    toast.error(response.payload.message);
                }
                loadResponse(id);
            })
            .finally(() => setLoading(false));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendReply();
        }
    };

    return (
        <Layout>
            <Container className="mt-4 px-4 mb-4 d-grid gap-5">
                <Col md={12} className="d-flex align-items-center justify-content-between">
                    <button 
                        type="button" 
                        className="btn-plain bg-white p-3 rounded-circle d-flex align-items-center justify-content-center" 
                        style={{ height: "50px", width: "50px" }}
                        onClick={() => navigate('/tickets')}
                    >
                        <FontAwesomeIcon icon="chevron-left" />
                    </button>
                </Col>
                <Card className="border-0 shadow">
                    <Card.Header>
                        <h5>
                            <span className="pe-3">Ticket - #{id}</span>
                            <Badge className="badge badge-active m-auto p-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="11" viewBox="0 0 10 11" fill="none">
                                    <circle cx="5" cy="5.5" r="5" fill="#008000"/>
                                </svg>
                                <span className="px-1">{ticket?.status}</span>
                            </Badge>
                        </h5>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column" style={{ minHeight: '500px' }}>
                        <Row className="flex-grow-1">
                            <Col md={7} className="d-flex flex-column">
                                <h4 className="text-muted mb-3">Chat</h4>
                                <div className="flex-grow-1 overflow-auto mb-3" style={{ maxHeight: '500px' }}>
                                    {replies?.length > 0 ? (
                                        replies.map((reply, index) => (
                                            <div key={index} className="d-flex mb-2">
                                                <img 
                                                    src={reply.photo || 'https://placehold.co/100'}  
                                                    width={50} 
                                                    alt={reply.id} 
                                                    className="rounded-circle" 
                                                    style={{ height: "40px", width: "40px" }}
                                                />
                                                <div className="ms-3">
                                                    <h6 className="mb-0">{reply.last_replied}</h6>
                                                    <p className={`${reply.attachments.length ? 'mb-0' : ''} text-sm text-muted`}>
                                                        {reply.message}
                                                    </p>
                                                    {reply.attachments?.map((attachment, idx) => (
                                                        <a 
                                                            href={attachment} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            key={idx} 
                                                            className="text-primary text-sm m-0 d-block"
                                                        >
                                                            View attachment
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No messages yet</p>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                                
                                <div className="mt-auto">
                                    {/* <div className="name-tag">A</div> */}
                                    <InputGroup>
                                        <Form.Control
                                            as="textarea"
                                            rows={1}
                                            aria-label="Write your comment"
                                            placeholder="Write your comment"
                                            name="message"
                                            onChange={handleInput}
                                            onKeyPress={handleKeyPress}
                                            value={payload.message}
                                        />
                                        {/* <InputGroup.Text className="bg-white text-primary">
                                            <i className="lni lni-paperclip"></i>
                                        </InputGroup.Text> */}
                                        <div>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                onClick={sendReply}
                                                disabled={!payload.message || loading}
                                            >
                                                {loading ? (
                                                    <Spinner animation="border" role="status" size="sm" />
                                                ) : (
                                                    <FontAwesomeIcon icon="paper-plane" />
                                                )}
                                            </Button>
                                        </div>
                                    </InputGroup>
                                </div>
                            </Col>
                            <Col md={5}>
                                <div className="bg-light h-100 px-3 py-2 modal-profile rounded">
                                    <ul className="list-unstyled">
                                        <li>
                                            <div>
                                                <p className="pt-2">
                                                    <small className="text-muted">Subject</small>
                                                    <strong className="d-block">{ticket?.subject}</strong>
                                                </p>
                                            </div>
                                        </li>
                                        <li>
                                            <div>
                                                <p className="pt-2">
                                                    <small className="text-muted">Sender</small>
                                                    <strong className="d-block">{ticket?.full_name || 'Nil'}</strong>
                                                </p>
                                            </div>
                                        </li>
                                        <li>
                                            <div>
                                                <p className="pt-2">
                                                    <small className="text-muted">Handled by</small>
                                                    <strong className="d-block">{ticket?.desk || 'Nil'}</strong>
                                                </p>
                                            </div>
                                        </li>
                                        <li>
                                            <div>
                                                <p className="pt-2">
                                                    <small className="text-muted">Issued date</small>
                                                    <strong className="d-block">{dateFormatter(ticket?.date)}</strong>
                                                </p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Container>
        </Layout>
    );
};

export default TicketView;